import React from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Icon,
  useColorModeValue,
  SimpleGrid,
} from '@chakra-ui/react';
import { FiDollarSign, FiShoppingBag, FiTag } from 'react-icons/fi';

const PricePreview = ({ products = [], discounts = { type: 'percentage', value: 0 } }) => {
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  // Get currency info from qbndAdmin with fallback
  const currencyInfo = window.qbndAdmin?.currency || { symbol: '$', position: 'left' };
  const { symbol, position, debug } = currencyInfo;

  // Function to format price with currency
  const formatPrice = (price) => {
    const formattedPrice = price.toFixed(2);
    const space = position.includes('space') ? ' ' : '';

    // Get currency code for special handling
    const currencyCode = debug?.currency_code?.toLowerCase() || '';

    // Handle special characters and HTML entities in symbol
    let displaySymbol = symbol;

    // If symbol is HTML entity or special character, use proper symbol
    if (symbol.includes('&') || /[^\x20-\x7E]/.test(symbol)) {
      switch (currencyCode) {
        case 'usd':
          displaySymbol = '$';
          break;
        case 'eur':
          displaySymbol = '€';
          break;
        case 'gbp':
          displaySymbol = '£';
          break;
        case 'jpy':
          displaySymbol = '¥';
          break;
        case 'bam':
          displaySymbol = 'KM';
          break;
        default:
          // If no special handling, try to decode HTML entity
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = symbol;
          displaySymbol = tempDiv.textContent || currencyCode;
      }
    }

    // Format based on position with proper spacing
    switch (position) {
      case 'left':
        return `${displaySymbol}${formattedPrice}`;
      case 'left_space':
        return `${displaySymbol}${space}${formattedPrice}`;
      case 'right':
        return `${formattedPrice}${displaySymbol}`;
      case 'right_space':
        return `${formattedPrice}${space}${displaySymbol}`;
      default:
        return `${displaySymbol}${formattedPrice}`;
    }
  };

  // Calculate totals excluding free products
  const originalTotal = products.reduce((sum, product) => {
    if (!product.isFree) {
      return sum + parseFloat(product.price || 0);
    }
    return sum;
  }, 0);

  const discountAmount = discounts.type === 'percentage'
    ? (originalTotal * (discounts.value / 100))
    : discounts.value;

  const finalPrice = Math.max(originalTotal - discountAmount, 0);

  // Count free products
  const freeProductsCount = products.filter(p => p.isFree).length;

  const PriceCard = ({ label, value, icon, color = 'blue.500', subtext }) => (
    <Box
      p={6}
      bg={bgColor}
      borderRadius="xl"
      boxShadow="sm"
      border="1px"
      borderColor={borderColor}
      transition="all 0.2s"
      _hover={{ transform: 'translateY(-2px)', boxShadow: 'md' }}
    >
      <VStack align="start" spacing={2}>
        <HStack spacing={3}>
          <Icon as={icon} color={color} boxSize={5} />
          <Text color="gray.500" fontSize="sm">{label}</Text>
        </HStack>
        <Text fontSize="2xl" fontWeight="bold" color={color}>
          {formatPrice(value)}
        </Text>
        {subtext && (
          <Text fontSize="sm" color="gray.500">
            {subtext}
          </Text>
        )}
      </VStack>
    </Box>
  );

  return (
    <Box>
      {/* Price Summary Cards */}
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
        <PriceCard
          label="Original Total"
          value={originalTotal}
          icon={FiShoppingBag}
          subtext={`${products.length} products${freeProductsCount > 0 ? ` (${freeProductsCount} free)` : ''}`}
        />
        <PriceCard
          label="Discount Amount"
          value={discountAmount}
          icon={FiTag}
          color="orange.500"
          subtext={discounts.type === 'percentage'
            ? `${discounts.value}% off`
            : 'Fixed amount off'}
        />
        <PriceCard
          label="Final Price"
          value={finalPrice}
          icon={FiDollarSign}
          color="green.500"
          subtext={freeProductsCount > 0 ? `Plus ${freeProductsCount} free product${freeProductsCount > 1 ? 's' : ''}` : 'After discount'}
        />
      </SimpleGrid>
    </Box>
  );
};

export default PricePreview;